import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { mockItems } from '../lib/mockData';
import type { Item, ItemFilters } from '../types';

export function useItems(filters?: ItemFilters) {
  const [items, setItems] = useState<Item[]>(mockItems);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchItems() {
      setLoading(true);
      setError(null);

      try {
        if (!isSupabaseConfigured) {
          let filtered = [...mockItems];
          if (filters?.search) {
            const search = filters.search.toLowerCase();
            filtered = filtered.filter(
              (item) =>
                item.name.toLowerCase().includes(search) ||
                item.category.toLowerCase().includes(search) ||
                item.tags.some((tag) => tag.toLowerCase().includes(search))
            );
          }
          if (filters?.category) {
            filtered = filtered.filter((item) =>
              item.category.toLowerCase().includes(filters.category.toLowerCase())
            );
          }
          if (filters?.minPrice != null) {
            filtered = filtered.filter(
              (item) => item.low_price !== null && item.low_price >= filters.minPrice!
            );
          }
          if (filters?.maxPrice != null) {
            filtered = filtered.filter(
              (item) => item.high_price !== null && item.high_price <= filters.maxPrice!
            );
          }
          if (isMounted) setItems(filtered);
          return;
        }

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Items query timeout')), 5000)
        );

        let query = supabase.from('catalogue_items').select('*');
        if (filters?.search) query = query.ilike('name', `%${filters.search}%`);
        if (filters?.category) query = query.ilike('category', `%${filters.category}%`);
        if (filters?.minPrice != null) query = query.gte('low_price', filters.minPrice);
        if (filters?.maxPrice != null) query = query.lte('high_price', filters.maxPrice);

        const queryPromise = query.order('name');
        const { data, error: fetchError } = await Promise.race([queryPromise, timeoutPromise]) as { data: Item[] | null; error: unknown };

        if (!isMounted) return;
        if (fetchError) throw fetchError;
        setItems(data || []);
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch items');
        setItems(mockItems);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchItems();
    return () => { isMounted = false; };
  }, [filters?.search, filters?.category, filters?.minPrice, filters?.maxPrice]);

  return { items, loading, error };
}

export function useItem(id: number | string) {
  const [item, setItem] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchItem() {
      setLoading(true);
      setError(null);

      try {
        if (!isSupabaseConfigured) {
          const found = mockItems.find((i) => i.id === Number(id));
          setItem(found || null);
          return;
        }

        const { data, error: fetchError } = await supabase
          .from('catalogue_items')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) throw fetchError;
        setItem(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch item');
        // Fallback to mock data
        const found = mockItems.find((i) => i.id === Number(id));
        setItem(found || null);
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      fetchItem();
    }
  }, [id]);

  return { item, loading, error };
}
